import { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Popconfirm, Progress, Row, Statistic, Tag, Typography } from 'antd';
import type { VerificationJobItem } from '@crm/shared';
import { CardTitle } from '../../components/StatCard';
import { plural } from '../../utils/format';

const CHECKER: Record<string, string> = { builtin: 'built-in checks', emailable: 'Emailable', myemailverifier: 'MyEmailVerifier', zerobounce: 'ZeroBounce' };
/** Queued longer than this usually means the worker isn't running. */
const QUEUED_WARN_MS = 60_000;
/** A chunk is 25–100 contacts; this long without a counter update means something is stuck. */
const STALLED_WARN_MS = 120_000;

export const isActiveJob = (j: VerificationJobItem) => j.status === 'PENDING' || j.status === 'RUNNING';

export function fmtDuration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

/** Elapsed time, contacts per minute and estimated time left, from the job's own timestamps. */
export function jobTiming(job: VerificationJobItem, now: number) {
  const started = job.startedAt ? Date.parse(job.startedAt) : null;
  const elapsed = started ? Math.max(0, (job.finishedAt ? Date.parse(job.finishedAt) : now) - started) : null;
  // under a few seconds the rate is noise (a 20-contact job finishing instantly is not "70,000 / min")
  const perMinute = elapsed && elapsed >= 5_000 && job.processed ? job.processed / (elapsed / 60_000) : null;
  const remaining = perMinute && job.total > job.processed ? ((job.total - job.processed) / perMinute) * 60_000 : null;
  return { elapsed, perMinute, remaining };
}

/** Re-renders every second while `active`, so elapsed time and warnings stay current between polls. */
function useNow(active: boolean) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active]);
  return active ? now : Date.now();
}

function activityText(job: VerificationJobItem): string {
  if (job.status === 'PENDING') return 'Queued, waiting for the worker to pick it up';
  if (job.status === 'RUNNING') {
    if (!job.resolvedVendor) return 'Preparing the contact list…';
    if (job.resolvedVendor === 'builtin') return 'Running built-in checks (syntax, disposable domains, mail server)';
    return `Confirming mailboxes with ${CHECKER[job.resolvedVendor] ?? job.resolvedVendor} (built-in checks only run on addresses not checked before)`;
  }
  if (job.status === 'COMPLETED') return 'Finished';
  if (job.status === 'STOPPED') return 'Stopped. Contacts checked so far keep their result';
  return `Failed: ${job.error ?? 'unknown error'}`;
}

export function VerificationProgress({ job, onStop, stopping }: { job: VerificationJobItem; onStop: () => void; stopping: boolean }) {
  const active = isActiveJob(job);
  const now = useNow(active);
  const { elapsed, perMinute, remaining } = jobTiming(job, now);
  const pct = job.total ? Math.round((job.processed / job.total) * 100) : job.status === 'COMPLETED' ? 100 : 0;
  const queuedFor = now - Date.parse(job.createdAt);
  const sinceUpdate = now - Date.parse(job.updatedAt);
  // jobs are queued with only the contacts that need checking, so the total is the real count
  const label = job.scope.label ?? (job.scope.campaignId ? 'Campaign audience' : `${plural(job.total, 'contact')} to check`);

  return (
    <Card
      style={{ marginBottom: 24 }}
      title={<CardTitle title={active ? 'Verification in progress' : 'Last verification'} subtitle={label} />}
      extra={
        active && (
          <Popconfirm title="Stop this verification?" description="Contacts checked so far keep their result." okText="Stop" onConfirm={onStop}>
            <Button danger loading={stopping}>Stop</Button>
          </Popconfirm>
        )
      }
    >
      <Typography.Text type={job.status === 'FAILED' ? 'danger' : 'secondary'}>{activityText(job)}</Typography.Text>
      <Progress
        percent={pct}
        status={job.status === 'FAILED' ? 'exception' : job.status === 'RUNNING' ? 'active' : job.status === 'COMPLETED' ? 'success' : 'normal'}
        format={() => `${job.processed.toLocaleString()} / ${job.total.toLocaleString()}`}
        style={{ margin: '8px 0 16px' }}
      />
      {job.status === 'PENDING' && queuedFor > QUEUED_WARN_MS && (
        <Alert type="warning" showIcon style={{ marginBottom: 16 }} title={`Still queued after ${fmtDuration(queuedFor)}`} description="The background worker hasn't picked this job up. Check that the worker service (crm-worker) is running." />
      )}
      {job.status === 'RUNNING' && sinceUpdate > STALLED_WARN_MS && (
        <Alert type="warning" showIcon style={{ marginBottom: 16 }} title={`No progress for ${fmtDuration(sinceUpdate)}`} description="The verification vendor may be responding slowly, or the worker may have stopped. Check the worker logs if this continues." />
      )}
      <Row gutter={[24, 16]}>
        {[
          ['Valid', job.validCount.toLocaleString()],
          ['Invalid', job.invalidCount.toLocaleString()],
          ['Unknown / catch-all', job.unknownCount.toLocaleString()],
          ['Speed', perMinute ? `${Math.round(perMinute).toLocaleString()} / min` : '—'],
          ['Elapsed', elapsed !== null ? fmtDuration(elapsed) : '—'],
          ['Time left', active ? (remaining !== null ? `~${fmtDuration(remaining)}` : 'Estimating…') : '—'],
        ].map(([title, value]) => (
          <Col xs={12} sm={8} lg={4} key={title}>
            <Statistic title={title} value={value} styles={{ content: { fontSize: 20 } }} />
          </Col>
        ))}
      </Row>
      {job.resolvedVendor && (
        <div style={{ marginTop: 12 }}>
          <Tag>{job.resolvedVendor === 'builtin' ? 'Built-in only (free)' : `Vendor: ${CHECKER[job.resolvedVendor] ?? job.resolvedVendor}`}</Tag>
        </div>
      )}
    </Card>
  );
}
